import { useEffect, useMemo, useRef, useState } from 'react';
import './MeshtasticWeatherStation.css';
import MeshtasticWeatherHistoryChart from './MeshtasticWeatherHistoryChart';
import {
  buildChartHistoryCsv,
  buildChartHistoryUrl,
  getChartHistoryMeta,
} from './meshtasticWeatherHistory';
import { downloadWeatherExport, requestWeatherHistoryExport } from './meshtasticWeatherExport';
import {
  getObservationArrivalTimestampMs,
  getObservationDisplayTimestampMs,
  parseWeatherTimestampMs as parseTimestampMs,
} from './meshtasticWeatherTimestamps';
import MeshtasticWeatherIconSummary, {
  getCivilTwilightDetails,
  getMoonPhaseDetails,
  resolveWeatherSummary,
} from './MeshtasticWeatherIconSummary';
import {
  convertPrecipitationValue,
  formatDistanceValue,
  formatFeelsLikeTemperatureValue,
  formatPrecipitationValue,
  formatPressureValue,
  formatTemperatureValue,
  formatWindSpeedValue,
  isPrecipitationMetricKey,
  isWindMetricKey,
  toNumber,
} from './meshtasticWeatherUnits';

const WEATHER_STATION_URL = process.env.REACT_APP_WEATHER_STATION_URL || '';
const WEATHER_STATION_HISTORY_URL = process.env.REACT_APP_WEATHER_STATION_HISTORY_URL || '';
const WEATHER_STATION_API_KEY = process.env.REACT_APP_WEATHER_STATION_API_KEY || '';
const WEATHER_STATION_PROJECT_URL = 'https://hackaday.io/project/205363-meshtastic-weather-station';
const WEATHER_STATION_PUBLIC_DASHBOARD_URL = 'https://www.wunderground.com/dashboard/pws/KPAPHILA4002';
const WEATHER_STATION_WINDY_DASHBOARD_URL = 'https://www.windy.com/station/pws-M5HsNWtn?40.036,-75.222,5';
const WEATHER_STATION_NOAA_MADIS_URL =
  'https://madis-data.ncep.noaa.gov/MadisSurface/?CenterLAT=39.97&CenterLON=-75.14&Zoom=16.00&networks=ALL.ALL&StationID=G7606';
const WEATHER_STATION_TIME_ZONE = 'America/New_York';
const WEATHER_STATION_LATITUDE = 39.97276832377213;
const WEATHER_STATION_LONGITUDE = -75.13826702552127;
const SUNRISE_SUNSET_API_URL = 'https://api.sunrise-sunset.org/json';
const SUN_TIMES_SOURCE_NAME = 'Sunrise-Sunset.org';
const SUN_TIMES_SOURCE_URL = 'https://sunrise-sunset.org/';
const AUTO_REFRESH_MS = 5 * 60 * 1000;
const HISTORY_REQUEST_DEBOUNCE_MS = 200;
const HISTORY_SAMPLE_MIN = 200;
const HISTORY_SAMPLE_MAX = 800;
const HISTORY_SAMPLE_BUCKET_PX = 50;
const HISTORY_CACHE_LIVE_STALE_MS = 60 * 1000;
const HISTORY_CACHE_ARCHIVE_STALE_MS = 10 * 60 * 1000;
const DAILY_HISTORY_LIMIT = 1000;
const DAILY_HISTORY_CACHE_STALE_MS = 60 * 1000;
const EMPTY_HISTORY_RAIN_TOTAL = {
  isLoading: false,
  rainEventItems: [],
  valueMm: null,
};
const HEALTHY_READING_MAX_AGE_MS = 10 * 60 * 1000;
const WARNING_READING_MAX_AGE_MS = 60 * 60 * 1000;
const EMPTY_HISTORY_META = {
  rawCount: 0,
  displayedCount: 0,
  sampled: false,
  mode: 'detail',
  resolution: 'raw',
  sampleRequested: null,
};
const UNIT_SYSTEM_PRESETS = {
  imperial: {
    label: 'Imperial',
    temperatureUnit: 'f',
    windSpeedUnit: 'mph',
    precipitationUnit: 'in',
    pressureUnit: 'inhg',
    distanceUnit: 'mi',
  },
  metric: {
    label: 'Metric',
    temperatureUnit: 'c',
    windSpeedUnit: 'ms',
    precipitationUnit: 'mm',
    pressureUnit: 'hpa',
    distanceUnit: 'km',
  },
};
const HISTORY_WINDOW_PRESETS = [
  { key: '1h', label: '1 Hour', hours: 1 },
  { key: '24h', label: '1 Day', hours: 24 },
  { key: '7d', label: '7 Days', hours: 24 * 7 },
  { key: '10d', label: '10 Days', hours: 24 * 10 },
  { key: '30d', label: '1 Month', hours: 24 * 30 },
];
const CUSTOM_HISTORY_WINDOW_KEY = 'custom';
const HISTORY_WINDOW_OPTIONS = [
  ...HISTORY_WINDOW_PRESETS,
  { key: CUSTOM_HISTORY_WINDOW_KEY, label: 'Custom range', hours: 24 },
];
const WEATHER_METRICS = [
  { key: 'air_temp_c', label: 'Air Temp', suffix: ' C', decimals: 1, featured: true },
  { key: 'relative_humidity_pct', label: 'Humidity', suffix: '%', decimals: 0, featured: true },
  { key: 'station_pressure_hpa', label: 'Pressure', suffix: ' hPa', decimals: 1, featured: true },
  { key: 'wind_avg_ms', label: 'Wind Avg', suffix: ' m/s', decimals: 1, featured: true, kind: 'wind' },
  { key: 'wind_gust_ms', label: 'Wind Gust', suffix: ' m/s', decimals: 1, featured: true, kind: 'wind' },
  { key: 'wind_lull_ms', label: 'Wind Lull', suffix: ' m/s', decimals: 1, kind: 'wind' },
  { key: 'wind_dir_deg', label: 'Wind Dir', suffix: ' deg', decimals: 0, featured: true },
  { key: 'rain_interval_mm', label: 'Rain Interval', suffix: ' mm', decimals: 1, featured: true, kind: 'precipitation' },
  { key: 'nearcast_rain_mm', label: 'Nearcast Rain', suffix: ' mm', decimals: 1, kind: 'precipitation' },
  { key: 'local_day_rain_mm', label: 'Local Day Rain', suffix: ' mm', decimals: 1, featured: true, kind: 'precipitation' },
  { key: 'local_day_nearcast_rain_mm', label: 'Local Day Nearcast Rain', suffix: ' mm', decimals: 1, kind: 'precipitation' },
  { key: 'solar_radiation_wm2', label: 'Solar Radiation', suffix: ' W/m2', decimals: 1, featured: true },
  { key: 'illuminance_lux', label: 'Illuminance', suffix: ' lux', decimals: 0 },
  { key: 'uv_index', label: 'UV Index', kind: 'plain', featured: true },
  { key: 'battery_voltage_v', label: 'Battery Voltage', suffix: ' V', decimals: 2, featured: true },
  { key: 'lightning_strike_count', label: 'Lightning Strikes', kind: 'plain', featured: true },
  { key: 'lightning_avg_distance_km', label: 'Lightning Avg Distance', suffix: ' km', decimals: 1 },
  { key: 'report_interval_min', label: 'Report Interval', suffix: ' min', decimals: 0 },
  { key: 'precipitation_type', label: 'Precipitation Type', kind: 'plain' },
  { key: 'precipitation_analysis_type', label: 'Precipitation Analysis Type', kind: 'plain' },
  { key: 'timestamp', label: 'Weather Timestamp', kind: 'timestamp' },
];
const WEATHER_METRIC_KEYS = new Set(WEATHER_METRICS.map((metric) => metric.key));
const SUMMARY_FIELDS = [
  { label: 'Temperature', patterns: ['temperature', 'temp'] },
  { label: 'Humidity', patterns: ['humidity', 'humid'] },
  { label: 'Pressure', patterns: ['pressure', 'barometer'] },
  { label: 'Wind Speed', patterns: ['windspeed', 'wind_speed', 'wind-speed'] },
  { label: 'Wind Direction', patterns: ['winddirection', 'wind_direction', 'winddir'] },
  { label: 'Rainfall', patterns: ['rainfall', 'rain_rate', 'rainrate', 'precip'] },
  { label: 'Battery', patterns: ['battery', 'voltage'] },
  { label: 'RSSI', patterns: ['rssi'] },
  { label: 'SNR', patterns: ['snr'] },
];
const DAILY_RAIN_PRESENT_MM = 0.25;
const DAILY_BREEZY_WIND_AVG_MS = 4;
const DAILY_WINDY_WIND_AVG_MS = 8;
const DAILY_BREEZY_WIND_GUST_MS = 7;
const DAILY_WINDY_WIND_GUST_MS = 12;
const DAILY_RAIN_TOTAL_KEYS = ['local_day_rain_mm', 'local_day_nearcast_rain_mm'];
const DAILY_RAIN_INTERVAL_KEYS = ['rain_interval_mm', 'rain_mm'];
const DAILY_WIND_AVERAGE_KEYS = ['wind_avg_ms', 'wind_ms'];
const DAILY_WIND_GUST_KEYS = ['wind_gust_ms', 'wind_gust_mps', 'gust_ms', 'wind_peak_ms', 'peak_wind_ms'];
const TEMP_TREND_THRESHOLD_C = 1;
const WIND_TREND_THRESHOLD_MS = 1.5;
const PRESSURE_TREND_THRESHOLD_HPA = 1;

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function flattenPayload(value, prefix = '') {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return prefix ? [{ path: prefix, value: '[]' }] : [];
    }

    return value.flatMap((entry, index) => flattenPayload(entry, `${prefix}[${index}]`));
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return prefix ? [{ path: prefix, value: '{}' }] : [];
    }

    return entries.flatMap(([key, nestedValue]) => (
      flattenPayload(nestedValue, prefix ? `${prefix}.${key}` : key)
    ));
  }

  return [{ path: prefix || 'response', value }];
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'n/a';
  }

  if (typeof value === 'number') {
    if (Number.isInteger(value)) {
      return value.toString();
    }

    return value
      .toFixed(Math.abs(value) >= 100 ? 1 : 2)
      .replace(/\.0$/, '')
      .replace(/(\.\d)0$/, '$1');
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'string') {
    const normalized = value.trim();

    if (/^\d{10}(\.\d+)?$/.test(normalized)) {
      return new Date(Number(normalized) * 1000).toLocaleString();
    }

    if (/^\d{13}$/.test(normalized)) {
      return new Date(Number(normalized)).toLocaleString();
    }

    const parsed = Date.parse(normalized);

    if (!Number.isNaN(parsed) && /\d{4}-\d{2}-\d{2}/.test(normalized)) {
      return new Date(parsed).toLocaleString();
    }

    return normalized;
  }

  return JSON.stringify(value);
}

function formatStationTimeOfDay(timestampMs) {
  if (!Number.isFinite(timestampMs)) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: WEATHER_STATION_TIME_ZONE,
    }).format(new Date(timestampMs));
  } catch (error) {
    return new Date(timestampMs).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}

function formatStationFullDate(timestampMs) {
  if (!Number.isFinite(timestampMs)) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'long',
      timeZone: WEATHER_STATION_TIME_ZONE,
      year: 'numeric',
    }).format(new Date(timestampMs));
  } catch (error) {
    return new Date(timestampMs).toLocaleDateString([], {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
}

function formatStationFullTime(timestampMs) {
  if (!Number.isFinite(timestampMs)) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: true,
      minute: '2-digit',
      second: '2-digit',
      timeZone: WEATHER_STATION_TIME_ZONE,
    }).format(new Date(timestampMs));
  } catch (error) {
    return new Date(timestampMs).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  }
}

function formatReadingAge(timestampMs, nowMs = Date.now()) {
  if (!Number.isFinite(timestampMs) || !Number.isFinite(nowMs)) {
    return 'No fresh reading yet';
  }

  const ageMs = Math.max(0, nowMs - timestampMs);
  const ageMinutes = Math.floor(ageMs / (60 * 1000));

  if (ageMinutes < 1) {
    return 'Just now';
  }

  if (ageMinutes < 60) {
    return `${ageMinutes}m ago`;
  }

  const ageHours = Math.floor(ageMinutes / 60);

  if (ageHours < 24) {
    return `${ageHours}h ago`;
  }

  return `${Math.floor(ageHours / 24)}d ago`;
}

export function getLatestReadingDetails(timestampMs, nowMs = Date.now()) {
  if (!Number.isFinite(timestampMs)) {
    return {
      ageLabel: 'No fresh reading yet',
      dateLabel: 'Waiting for data',
      timeLabel: '',
    };
  }

  return {
    ageLabel: formatReadingAge(timestampMs, nowMs),
    dateLabel: formatStationFullDate(timestampMs),
    timeLabel: formatStationFullTime(timestampMs),
  };
}

function humanizePath(path) {
  return path
    .replace(/\[(\d+)\]/g, ' $1 ')
    .split('.')
    .map((segment) => segment
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]/g, ' ')
      .trim())
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' / ');
}

function findFirstMatchingEntry(entries, patterns) {
  return entries.find(({ path }) => {
    const normalizedPath = path.toLowerCase();
    return patterns.some((pattern) => normalizedPath.includes(pattern));
  });
}

function formatMetricValue(value, suffix, decimals) {
  if (value === null || value === undefined || value === '') {
    return 'n/a';
  }

  if (typeof value !== 'number') {
    return `${formatValue(value)}${suffix}`;
  }

  if (Number.isInteger(value) || decimals === 0) {
    return `${value.toFixed(decimals)}${suffix}`;
  }

  return `${value.toFixed(decimals).replace(/(\.\d)0$/, '$1')}${suffix}`;
}

function formatWeatherMetric(
  metric,
  value,
  temperatureUnit = 'c',
  windSpeedUnit = 'ms',
  precipitationUnit = 'mm',
  pressureUnit = 'hpa',
  distanceUnit = 'km',
) {
  if (metric.kind === 'timestamp' || metric.kind === 'plain') {
    return formatValue(value);
  }

  if (metric.key === 'air_temp_c') {
    return formatTemperatureValue(value, temperatureUnit, metric.decimals ?? 1);
  }

  if (metric.kind === 'wind' || isWindMetricKey(metric.key)) {
    return formatWindSpeedValue(value, windSpeedUnit, metric.decimals ?? 1);
  }

  if (metric.kind === 'precipitation' || isPrecipitationMetricKey(metric.key)) {
    return formatPrecipitationValue(value, precipitationUnit, metric.decimals ?? 1);
  }

  if (metric.key === 'station_pressure_hpa') {
    return formatPressureValue(value, pressureUnit, metric.decimals ?? 1);
  }

  if (metric.key === 'lightning_avg_distance_km') {
    return formatDistanceValue(value, distanceUnit, metric.decimals ?? 1);
  }

  return formatMetricValue(value, metric.suffix || '', metric.decimals ?? 0);
}

function WeatherUiIcon({ name, className = '' }) {
  const knownIcons = [
    'sun',
    'readings',
    'history',
    'details',
    'health',
    'clock',
    'temperature',
    'humidity',
    'wind',
    'pressure',
    'rain',
    'sky',
    'battery',
    'article',
    'globe',
    'chevron-up',
    'chevron-down',
  ];

  return (
    <span className={`weather-ui-icon ${className}`.trim()} aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        {name === 'sun' && (
          <>
            <circle cx="12" cy="12" r="4.2" />
            <path d="M12 2.8v2.4M12 18.8v2.4M4.2 4.2l1.7 1.7M18.1 18.1l1.7 1.7M2.8 12h2.4M18.8 12h2.4M4.2 19.8l1.7-1.7M18.1 5.9l1.7-1.7" />
          </>
        )}
        {name === 'readings' && (
          <>
            <path d="M5 19V5" />
            <path d="M5 19h14" />
            <path d="M8 16v-4M12 16V8M16 16v-6" />
          </>
        )}
        {name === 'history' && (
          <>
            <path d="M4 19V5" />
            <path d="M4 17.5h16" />
            <path d="m7 14 3.3-3.4 3 2.2L18 7.5" />
          </>
        )}
        {name === 'details' && (
          <>
            <rect x="4" y="5" width="16" height="14" rx="1.5" />
            <path d="M4 10h16M9 5v14M14 5v14" />
          </>
        )}
        {name === 'health' && (
          <path d="M12 21s7-3.5 7-10V5.8L12 3 5 5.8V11c0 6.5 7 10 7 10Z" />
        )}
        {name === 'clock' && (
          <>
            <circle cx="12" cy="12" r="7.5" />
            <path d="M12 8v4.2l2.8 1.6" />
          </>
        )}
        {name === 'temperature' && (
          <>
            <path d="M10 14.2V5.5a2 2 0 0 1 4 0v8.7" />
            <path d="M8.2 17.2a3.8 3.8 0 1 0 7.6 0c0-1.3-.7-2.4-1.8-3.1" />
          </>
        )}
        {name === 'humidity' && (
          <path d="M12 21a6.2 6.2 0 0 0 6.2-6.2C18.2 10.1 12 3 12 3s-6.2 7.1-6.2 11.8A6.2 6.2 0 0 0 12 21Z" />
        )}
        {name === 'wind' && (
          <>
            <path d="M3.5 8.5h10.8c2.1 0 3.2-1.1 3.2-2.5s-1.1-2.5-2.6-2.5c-1.2 0-2.2.7-2.5 1.8" />
            <path d="M3.5 13h14.8c1.9 0 3.2 1.1 3.2 2.7s-1.3 2.8-3 2.8c-1.4 0-2.4-.7-2.8-1.9" />
            <path d="M3.5 17h7" />
          </>
        )}
        {name === 'pressure' && (
          <>
            <path d="M5 16a7 7 0 1 1 14 0" />
            <path d="m12 16 3.2-5.4" />
            <path d="M8.4 16h7.2" />
          </>
        )}
        {name === 'rain' && (
          <>
            <path d="M4 12a8 8 0 0 1 16 0Z" />
            <path d="M4 12c1.6 0 1.6-1.5 3.2-1.5S8.8 12 10.4 12s1.6-1.5 3.2-1.5 1.6 1.5 3.2 1.5S18.4 10.5 20 12" />
            <path d="M12 12v5.3a2.7 2.7 0 0 0 5.4 0" />
          </>
        )}
        {name === 'sky' && (
          <>
            <circle cx="8" cy="8" r="2.8" />
            <path d="M8 2.8v1.1M8 12.1v1.1M3.4 8H2.3M13.7 8h-1.1M4.7 4.7l-.8-.8M12.1 12.1l-.8-.8M4.7 11.3l-.8.8M12.1 3.9l-.8.8" />
            <path d="M8 17.5h8.5a3 3 0 0 0 .2-6A4 4 0 0 0 9.1 10" />
          </>
        )}
        {name === 'battery' && (
          <>
            <rect x="3.5" y="7.5" width="15" height="9" rx="1.6" />
            <path d="M20.5 10.2v3.6" />
            <rect className="weather-battery-level" x="6.4" y="10" width="9.5" height="4" rx="0.8" />
          </>
        )}
        {name === 'article' && (
          <>
            <path d="M7 3.5h7l3 3V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" />
            <path d="M14 3.5V7h3M9 11h6M9 14h6M9 17h4" />
          </>
        )}
        {name === 'globe' && (
          <>
            <circle cx="12" cy="12" r="8.2" />
            <path d="M3.8 12h16.4" />
            <path d="M12 3.8c2.2 2.2 3.3 4.9 3.3 8.2s-1.1 6-3.3 8.2" />
            <path d="M12 3.8C9.8 6 8.7 8.7 8.7 12s1.1 6 3.3 8.2" />
            <path d="M5.7 7.1c1.6.8 3.7 1.2 6.3 1.2s4.7-.4 6.3-1.2" />
            <path d="M5.7 16.9c1.6-.8 3.7-1.2 6.3-1.2s4.7.4 6.3 1.2" />
          </>
        )}
        {name === 'chevron-up' && <path d="m7 14 5-5 5 5" />}
        {name === 'chevron-down' && <path d="m7 10 5 5 5-5" />}
        {!knownIcons.includes(name) && (
          <>
            <circle cx="12" cy="12" r="7.5" />
            <path d="M12 8v4l2.8 2" />
          </>
        )}
      </svg>
    </span>
  );
}

function WeatherStationMark({ className = '' }) {
  return (
    <svg
      className={`weather-station-mark ${className}`.trim()}
      viewBox="0 0 64 48"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M14 34c8-18 20-24 36-22-2 16-10 26-26 28"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="5.4"
      />
      <path
        d="M18 36c8-8 18-13 31-18"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="5.4"
      />
    </svg>
  );
}

function RefreshIcon({ spinning = false }) {
  return (
    <svg
      className={`weather-refresh-icon ${spinning ? 'weather-refresh-icon--spinning' : ''}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M20 4v5h-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function degreesToCardinal(value) {
  const numeric = toNumber(value);

  if (numeric === null) {
    return 'Variable';
  }

  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const normalized = ((numeric % 360) + 360) % 360;
  return directions[Math.round(normalized / 45) % directions.length];
}

function makeMetricState(tone, status, note) {
  return { tone, status, note };
}

function formatUtcIso(date) {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function roundDownToMinute(date) {
  return new Date(Math.floor(date.getTime() / (60 * 1000)) * (60 * 1000));
}

function getHistorySampleSize(chartWidth) {
  if (!Number.isFinite(chartWidth) || chartWidth <= 0) {
    return null;
  }

  return clamp(
    Math.round(chartWidth / HISTORY_SAMPLE_BUCKET_PX) * HISTORY_SAMPLE_BUCKET_PX,
    HISTORY_SAMPLE_MIN,
    HISTORY_SAMPLE_MAX,
  );
}

function getHistoryCacheStaleMs(windowHours) {
  return windowHours <= 24
    ? HISTORY_CACHE_LIVE_STALE_MS
    : HISTORY_CACHE_ARCHIVE_STALE_MS;
}

export function normalizeHistoryWindowRange(range) {
  const fromMs = toNumber(range?.fromMs ?? range?.fromTimestampMs);
  const toMs = toNumber(range?.toMs ?? range?.toTimestampMs);

  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
    return null;
  }

  return {
    fromMs,
    toMs,
  };
}

export function getDefaultCustomHistoryWindowRange(nowMs = Date.now()) {
  const toDate = roundDownToMinute(new Date(nowMs));
  const toMs = toDate.getTime();

  return {
    fromMs: toMs - (24 * 60 * 60 * 1000),
    toMs,
  };
}

export function getSelectedHistoryWindowDetails(selectedWindowKey, customWindowRange) {
  const normalizedCustomRange = normalizeHistoryWindowRange(customWindowRange);

  if (selectedWindowKey === CUSTOM_HISTORY_WINDOW_KEY && normalizedCustomRange) {
    return {
      key: CUSTOM_HISTORY_WINDOW_KEY,
      label: 'Custom range',
      hours: (normalizedCustomRange.toMs - normalizedCustomRange.fromMs) / (60 * 60 * 1000),
      fromMs: normalizedCustomRange.fromMs,
      toMs: normalizedCustomRange.toMs,
    };
  }

  return (
    HISTORY_WINDOW_PRESETS.find((preset) => preset.key === selectedWindowKey) ||
    HISTORY_WINDOW_PRESETS[1]
  );
}

function getHistoryWindowDateRange(selectedHistoryWindow, nowMs = Date.now()) {
  if (
    selectedHistoryWindow?.key === CUSTOM_HISTORY_WINDOW_KEY &&
    Number.isFinite(selectedHistoryWindow.fromMs) &&
    Number.isFinite(selectedHistoryWindow.toMs)
  ) {
    return {
      fromDate: new Date(selectedHistoryWindow.fromMs),
      toDate: new Date(selectedHistoryWindow.toMs),
    };
  }

  const toDate = roundDownToMinute(new Date(nowMs));
  const fromDate = new Date(
    toDate.getTime() - ((selectedHistoryWindow?.hours || 24) * 60 * 60 * 1000),
  );

  return {
    fromDate,
    toDate,
  };
}

function getStationIdFromRequestUrl(requestUrl) {
  try {
    return new URL(requestUrl).searchParams.get('stationId') || '';
  } catch (error) {
    return '';
  }
}

function buildHistoryCacheKey(stationId, fromIso, toIso, sample) {
  return `${stationId}|${fromIso}|${toIso}|${sample}`;
}

function trimHistoryCache(cache, maxEntries = 12) {
  while (cache.size > maxEntries) {
    const oldestKey = cache.keys().next().value;
    if (!oldestKey) {
      return;
    }

    cache.delete(oldestKey);
  }
}

function buildHistoryRequestUrl(baseUrl, {
  stationId,
  fromIso,
  toIso,
  sample,
  limit,
  order,
  nextToken,
}) {
  const url = new URL(baseUrl);

  if (stationId) {
    url.searchParams.set('stationId', stationId);
  }

  if (fromIso) {
    url.searchParams.set('from', fromIso);
  }

  if (toIso) {
    url.searchParams.set('to', toIso);
  }

  if (sample !== null && sample !== undefined) {
    url.searchParams.set('sample', String(sample));
    url.searchParams.delete('limit');
    url.searchParams.delete('order');
    url.searchParams.delete('nextToken');
    return url.toString();
  }

  url.searchParams.delete('sample');

  if (limit !== null && limit !== undefined) {
    url.searchParams.set('limit', String(limit));
  } else {
    url.searchParams.delete('limit');
  }

  if (order) {
    url.searchParams.set('order', order);
  } else {
    url.searchParams.delete('order');
  }

  if (nextToken) {
    url.searchParams.set('nextToken', nextToken);
  } else {
    url.searchParams.delete('nextToken');
  }

  return url.toString();
}

function getHistoryResponseNextToken(payload) {
  const token =
    payload?.nextToken ??
    payload?.next_token ??
    payload?.pagination?.nextToken ??
    payload?.pagination?.next_token;

  if (token === null || token === undefined || token === '') {
    return '';
  }

  return typeof token === 'string' ? token : JSON.stringify(token);
}

function buildSunTimesRequestUrl(dateKey) {
  const url = new URL(SUNRISE_SUNSET_API_URL);
  url.searchParams.set('lat', String(WEATHER_STATION_LATITUDE));
  url.searchParams.set('lng', String(WEATHER_STATION_LONGITUDE));
  url.searchParams.set('date', dateKey);
  url.searchParams.set('formatted', '0');
  url.searchParams.set('tzid', WEATHER_STATION_TIME_ZONE);
  return url.toString();
}

function getResponseErrorMessage(payload, fallbackMessage) {
  if (typeof payload === 'string') {
    return payload;
  }

  if (payload && typeof payload === 'object') {
    return payload.error || payload.message || fallbackMessage;
  }

  return fallbackMessage;
}

function normalizeSunTimesPayload(payload) {
  if (payload?.status !== 'OK' || !isPlainObject(payload?.results)) {
    return null;
  }

  const sunsetTimestampMs = parseTimestampMs(payload.results.sunset);
  const civilTwilightEndTimestampMs = parseTimestampMs(payload.results.civil_twilight_end);

  if (!sunsetTimestampMs || !civilTwilightEndTimestampMs) {
    return null;
  }

  return {
    sunsetUtc: new Date(sunsetTimestampMs).toISOString(),
    civilTwilightEndUtc: new Date(civilTwilightEndTimestampMs).toISOString(),
    sourceName: SUN_TIMES_SOURCE_NAME,
    sourceUrl: SUN_TIMES_SOURCE_URL,
  };
}

function getHistoryObservationTimeMs(item) {
  return getObservationDisplayTimestampMs(item);
}

function getHistoryObservationKey(item) {
  return (
    item?.sk ||
    item?.latest_observation_sk ||
    `${item?.source_ts_sort_utc || item?.source_ts_utc || item?.source_ts_raw || 'unknown'}:${item?.msg_id ?? 'na'}`
  );
}

function dedupeHistoryItems(items) {
  const seenKeys = new Set();

  return items.filter((item) => {
    const key = getHistoryObservationKey(item);
    if (seenKeys.has(key)) {
      return false;
    }

    seenKeys.add(key);
    return true;
  });
}

function getStationDateKey(timestampMs) {
  if (!Number.isFinite(timestampMs)) {
    return '';
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: '2-digit',
      timeZone: WEATHER_STATION_TIME_ZONE,
      year: 'numeric',
    }).formatToParts(new Date(timestampMs));
    const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return `${partMap.year}-${partMap.month}-${partMap.day}`;
  } catch (error) {
    return new Date(timestampMs).toISOString().slice(0, 10);
  }
}

function getStationDateTimeParts(timestampMs) {
  const fallbackDate = new Date(timestampMs);

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      hour: '2-digit',
      hour12: false,
      hourCycle: 'h23',
      minute: '2-digit',
      month: '2-digit',
      second: '2-digit',
      timeZone: WEATHER_STATION_TIME_ZONE,
      year: 'numeric',
    }).formatToParts(fallbackDate);
    const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return {
      year: Number(partMap.year),
      month: Number(partMap.month),
      day: Number(partMap.day),
      hour: Number(partMap.hour) % 24,
      minute: Number(partMap.minute),
      second: Number(partMap.second),
    };
  } catch (error) {
    return {
      year: fallbackDate.getFullYear(),
      month: fallbackDate.getMonth() + 1,
      day: fallbackDate.getDate(),
      hour: fallbackDate.getHours(),
      minute: fallbackDate.getMinutes(),
      second: fallbackDate.getSeconds(),
    };
  }
}

function getStationTimeZoneOffsetMs(timestampMs) {
  const parts = getStationDateTimeParts(timestampMs);
  const timeZoneAsUtcMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );

  return timeZoneAsUtcMs - timestampMs;
}

export function getStationDayStartDate(timestampMs) {
  if (!Number.isFinite(timestampMs)) {
    return new Date(Number.NaN);
  }

  const parts = getStationDateTimeParts(timestampMs);
  const stationMidnightAsUtcMs = Date.UTC(parts.year, parts.month - 1, parts.day);
  let dayStartMs = stationMidnightAsUtcMs - getStationTimeZoneOffsetMs(stationMidnightAsUtcMs);

  dayStartMs = stationMidnightAsUtcMs - getStationTimeZoneOffsetMs(dayStartMs);

  return new Date(dayStartMs);
}

export function getStationDayHistoryWindow(timestampMs, nowMs = Date.now()) {
  if (!Number.isFinite(timestampMs)) {
    return null;
  }

  const fromDate = getStationDayStartDate(timestampMs);
  const fromTimestampMs = fromDate.getTime();

  if (!Number.isFinite(fromTimestampMs)) {
    return null;
  }

  const toTimestampMs =
    getStationDateKey(nowMs) === getStationDateKey(timestampMs)
      ? nowMs
      : fromTimestampMs + (24 * 60 * 60 * 1000) - 1;
  const toDate = getStationDateKey(nowMs) === getStationDateKey(timestampMs)
    ? roundDownToMinute(new Date(toTimestampMs))
    : new Date(toTimestampMs);

  return {
    fromDate,
    toDate,
  };
}

function getHistoryWeatherBlock(item) {
  const weather = item?.weather || item?.raw_payload?.payload?.weather;
  return isPlainObject(weather) ? weather : null;
}

function getHistoryExportStationId(item) {
  return (
    item?.source_node_id ||
    item?.pk?.replace(/^STATION#/, '') ||
    item?.raw_payload?.payload?.from ||
    ''
  );
}

function formatStationDateTimeForCsv(timestampMs) {
  if (!Number.isFinite(timestampMs) || timestampMs <= 0) {
    return '';
  }

  const parts = getStationDateTimeParts(timestampMs);
  const pad = (value) => String(value).padStart(2, '0');

  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)} ${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`;
}

function stringifyCsvValue(value) {
  if (value === null || value === undefined) {
    return '';
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function escapeCsvValue(value) {
  const stringValue = stringifyCsvValue(value);

  if (!/[",\r\n]/.test(stringValue)) {
    return stringValue;
  }

  return `"${stringValue.replace(/"/g, '""')}"`;
}

function getHistoryExportWeatherKeys(items) {
  const weatherKeys = new Set();

  items.forEach((item) => {
    const weather = getHistoryWeatherBlock(item);

    if (!weather) {
      return;
    }

    Object.keys(weather).forEach((key) => weatherKeys.add(key));
  });

  const orderedKnownKeys = WEATHER_METRICS
    .map((metric) => metric.key)
    .filter((key) => weatherKeys.has(key));
  const extraKeys = Array.from(weatherKeys)
    .filter((key) => !WEATHER_METRIC_KEYS.has(key))
    .sort((left, right) => left.localeCompare(right));

  return [...orderedKnownKeys, ...extraKeys];
}

export function buildWeatherHistoryCsv(historyItems) {
  const normalizedItems = dedupeHistoryItems(historyItems || [])
    .sort((left, right) => getHistoryObservationTimeMs(left) - getHistoryObservationTimeMs(right));
  const weatherKeys = getHistoryExportWeatherKeys(normalizedItems);
  const columns = [
    {
      header: 'observed_at_utc',
      value: (item) => {
        const timestampMs = getHistoryObservationTimeMs(item);
        return timestampMs ? new Date(timestampMs).toISOString() : '';
      },
    },
    {
      header: 'observed_at_station_time',
      value: (item) => formatStationDateTimeForCsv(getHistoryObservationTimeMs(item)),
    },
    {
      header: 'station_time_zone',
      value: () => WEATHER_STATION_TIME_ZONE,
    },
    {
      header: 'station_id',
      value: getHistoryExportStationId,
    },
    {
      header: 'msg_id',
      value: (item) => item?.msg_id ?? item?.message_id ?? '',
    },
    {
      header: 'received_at_utc',
      value: (item) => item?.received_at_utc ?? '',
    },
    {
      header: 'source_ts_utc',
      value: (item) => item?.source_ts_utc ?? item?.source_ts_sort_utc ?? '',
    },
    {
      header: 'source_ts_raw',
      value: (item) => item?.source_ts_raw ?? '',
    },
    ...weatherKeys.map((key) => ({
      header: `weather.${key}`,
      value: (item) => getHistoryWeatherBlock(item)?.[key] ?? '',
    })),
  ];

  return [
    columns.map((column) => escapeCsvValue(column.header)).join(','),
    ...normalizedItems.map((item) => (
      columns.map((column) => escapeCsvValue(column.value(item))).join(',')
    )),
  ].join('\r\n');
}

function formatFilenameDate(date) {
  const timestampMs = date instanceof Date ? date.getTime() : Date.parse(date);

  if (!Number.isFinite(timestampMs)) {
    return 'unknown';
  }

  return new Date(timestampMs)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z')
    .replace('T', '-');
}

export function buildWeatherHistoryCsvFilename(fromDate, toDate) {
  return `emmas-court-weather-history-${formatFilenameDate(fromDate)}-to-${formatFilenameDate(toDate)}.csv`;
}

function downloadCsvFile(csvContent, filename) {
  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 0);
}

function getFirstNumericWeatherValue(weather, keys) {
  for (const key of keys) {
    const numericValue = toNumber(weather?.[key]);

    if (numericValue !== null) {
      return numericValue;
    }
  }

  return null;
}

function getNumericWeatherValues(weather, keys) {
  return keys
    .map((key) => toNumber(weather?.[key]))
    .filter((value) => value !== null);
}

function getRainIntervalMm(weather) {
  const reportedInterval = getFirstNumericWeatherValue(weather, DAILY_RAIN_INTERVAL_KEYS);

  if (reportedInterval !== null) {
    return Math.max(reportedInterval, 0);
  }

  return null;
}

export function buildHistoryRainIntervalSummary(historyItems) {
  const points = (historyItems || [])
    .map((item) => {
      const weather = getHistoryWeatherBlock(item);
      const observedAtMs = getHistoryObservationTimeMs(item);

      return weather && observedAtMs ? { item, observedAtMs, weather } : null;
    })
    .filter(Boolean)
    .sort((left, right) => left.observedAtMs - right.observedAtMs);

  if (!points.length) {
    return {
      rainEventItems: [],
      valueMm: null,
    };
  }

  const summary = points.reduce((accumulator, point) => {
    const intervalValue = getRainIntervalMm(point.weather);

    if (intervalValue === null) {
      return accumulator;
    }

    if (intervalValue > 0) {
      return {
        hasIntervalData: true,
        rainEventItems: [...accumulator.rainEventItems, point.item],
        valueMm: accumulator.valueMm + intervalValue,
      };
    }

    return {
      ...accumulator,
      hasIntervalData: true,
    };
  }, {
    hasIntervalData: false,
    rainEventItems: [],
    valueMm: 0,
  });

  return {
    rainEventItems: summary.rainEventItems,
    valueMm: summary.hasIntervalData ? summary.valueMm : null,
  };
}

export function buildHistoryRainTotalMm(historyItems) {
  return buildHistoryRainIntervalSummary(historyItems).valueMm;
}

function formatTemperatureDelta(valueC, temperatureUnit) {
  const convertedValue = temperatureUnit === 'f' ? valueC * 1.8 : valueC;
  const suffix = temperatureUnit === 'f' ? ' F' : ' C';

  return formatMetricValue(convertedValue, suffix, 1);
}

function joinSummaryParts(parts) {
  const filteredParts = parts.filter(Boolean);

  if (filteredParts.length <= 1) {
    return filteredParts[0] || '';
  }

  if (filteredParts.length === 2) {
    return `${filteredParts[0]} and ${filteredParts[1]}`;
  }

  return `${filteredParts.slice(0, -1).join(', ')}, and ${filteredParts[filteredParts.length - 1]}`;
}

function capitalizeFirst(value) {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
}

function describeSkyCover(points) {
  const skyCounts = points.reduce((counts, point) => {
    const solarRadiation = toNumber(point.weather?.solar_radiation_wm2);
    const uvIndex = toNumber(point.weather?.uv_index);
    const illuminance = toNumber(point.weather?.illuminance_lux);
    const hasDaylightSignal =
      (solarRadiation !== null && solarRadiation > 20) ||
      (uvIndex !== null && uvIndex > 0) ||
      (illuminance !== null && illuminance > 1000);

    if (!hasDaylightSignal) {
      return counts;
    }

    if (
      (uvIndex !== null && uvIndex >= 4) ||
      (solarRadiation !== null && solarRadiation >= 450) ||
      (illuminance !== null && illuminance >= 10000)
    ) {
      return { ...counts, sunny: counts.sunny + 1 };
    }

    if (
      (uvIndex !== null && uvIndex <= 2) ||
      (solarRadiation !== null && solarRadiation < 200) ||
      (illuminance !== null && illuminance < 10000)
    ) {
      return { ...counts, cloudy: counts.cloudy + 1 };
    }

    return { ...counts, mixed: counts.mixed + 1 };
  }, { cloudy: 0, mixed: 0, sunny: 0 });
  const skySampleCount = skyCounts.cloudy + skyCounts.mixed + skyCounts.sunny;

  if (!skySampleCount) {
    return '';
  }

  if (skyCounts.sunny >= Math.max(skyCounts.cloudy, skyCounts.mixed) * 1.2) {
    return 'mostly sunny';
  }

  if (skyCounts.cloudy >= Math.max(skyCounts.sunny, skyCounts.mixed) * 1.2) {
    return 'mostly cloudy';
  }

  return 'mixed sun and clouds';
}

function getDailyRainStats(points) {
  const dailyRainValues = points.reduce((values, point) => (
    values.concat(getNumericWeatherValues(point.weather, DAILY_RAIN_TOTAL_KEYS))
  ), []);
  const intervalRainValues = points.reduce((values, point) => (
    values.concat(getNumericWeatherValues(point.weather, DAILY_RAIN_INTERVAL_KEYS))
  ), []);
  const hasRainType = points.some((point) => (
    (toNumber(point.weather?.precipitation_type) ?? 0) > 0
  ));
  const dailyRainTotal = dailyRainValues.length ? Math.max(...dailyRainValues) : null;
  const intervalRainTotal = intervalRainValues.reduce((total, value) => total + Math.max(value, 0), 0);
  const rainTotal = Math.max(dailyRainTotal ?? 0, intervalRainTotal);

  return {
    dailyRainTotal,
    hasRainData: dailyRainValues.length > 0 || intervalRainValues.length > 0 || hasRainType,
    hasRainType,
    intervalRainTotal,
    rainTotal,
  };
}

function describeWeatherEvent(points) {
  const rainStats = getDailyRainStats(points);
  const hasLightning = points.some((point) => (
    (toNumber(point.weather?.lightning_strike_count) ?? 0) > 0
  ));
  const hasMeasuredRain =
    rainStats.rainTotal >= DAILY_RAIN_PRESENT_MM ||
    rainStats.intervalRainTotal > 0 ||
    (rainStats.dailyRainTotal ?? 0) > 0;
  const hasRainSignal = hasMeasuredRain || rainStats.hasRainType;

  if (hasLightning && hasRainSignal) {
    return {
      key: 'thunderstorm',
      lightWindSummary: 'storm passed with light wind',
      skySummary: 'thunderstorm moved through',
    };
  }

  if (hasLightning) {
    return {
      key: 'lightning',
      lightWindSummary: 'lightning passed with light wind',
      skySummary: 'lightning nearby',
    };
  }

  if (hasRainSignal) {
    return {
      key: 'rain',
      lightWindSummary: 'rain passed with light wind',
      skySummary: 'rain clouds moved through',
    };
  }

  return null;
}

function describeRain(points, precipitationUnit) {
  const rainStats = getDailyRainStats(points);

  if (!rainStats.hasRainData) {
    return '';
  }

  if (rainStats.rainTotal >= DAILY_RAIN_PRESENT_MM) {
    return `rain total ${formatPrecipitationValue(rainStats.rainTotal, precipitationUnit, 1)}`;
  }

  if (
    rainStats.intervalRainTotal > 0 ||
    (rainStats.dailyRainTotal ?? 0) > 0 ||
    rainStats.hasRainType
  ) {
    return 'rain detected in sampled intervals';
  }

  return 'no rain detected';
}

function describeWind(points, windSpeedUnit, weatherEvent) {
  const averageWindValues = points.reduce((values, point) => (
    values.concat(getNumericWeatherValues(point.weather, DAILY_WIND_AVERAGE_KEYS))
  ), []);
  const gustValues = points.reduce((values, point) => (
    values.concat(getNumericWeatherValues(point.weather, DAILY_WIND_GUST_KEYS))
  ), []);
  const maxAverageWind = averageWindValues.length ? Math.max(...averageWindValues) : null;
  const maxGust = gustValues.length ? Math.max(...gustValues) : null;
  const lightWindLabel = weatherEvent?.lightWindSummary || 'wind has stayed light';

  if (maxAverageWind === null && maxGust === null) {
    return '';
  }

  const windLabel =
    (maxAverageWind !== null && maxAverageWind >= DAILY_WINDY_WIND_AVG_MS) ||
    (maxGust !== null && maxGust >= DAILY_WINDY_WIND_GUST_MS)
      ? 'windy'
      : (maxAverageWind !== null && maxAverageWind >= DAILY_BREEZY_WIND_AVG_MS) ||
        (maxGust !== null && maxGust >= DAILY_BREEZY_WIND_GUST_MS)
          ? 'breezy'
          : 'wind has stayed light';

  if (maxGust !== null && windLabel !== 'wind has stayed light') {
    return `${windLabel} with peak gusts near ${formatWindSpeedValue(maxGust, windSpeedUnit, 1)}`;
  }

  if (maxAverageWind !== null && windLabel !== 'wind has stayed light') {
    return `${windLabel} with sustained wind near ${formatWindSpeedValue(maxAverageWind, windSpeedUnit, 1)}`;
  }

  return lightWindLabel;
}

function describeTrend(points, temperatureUnit, windSpeedUnit, pressureUnit) {
  const numericSeries = (keys) => points
    .map((point) => getFirstNumericWeatherValue(point.weather, keys))
    .filter((value) => value !== null);
  const tempValues = numericSeries(['air_temp_c', 'temp_c']);
  const windValues = numericSeries(['wind_avg_ms', 'wind_ms']);
  const pressureValues = numericSeries(['station_pressure_hpa', 'pressure_hpa', 'barometer_hpa']);
  const trendParts = [];

  if (tempValues.length >= 2) {
    const tempDiff = tempValues[tempValues.length - 1] - tempValues[0];

    if (Math.abs(tempDiff) >= TEMP_TREND_THRESHOLD_C) {
      trendParts.push(
        `temperatures ${tempDiff > 0 ? 'warming' : 'cooling'} by ${formatTemperatureDelta(Math.abs(tempDiff), temperatureUnit)}`,
      );
    } else {
      trendParts.push('temperatures holding steady');
    }
  }

  if (windValues.length >= 2) {
    const windDiff = windValues[windValues.length - 1] - windValues[0];

    if (Math.abs(windDiff) >= WIND_TREND_THRESHOLD_MS) {
      trendParts.push(`wind ${windDiff > 0 ? 'building' : 'easing'} by ${formatWindSpeedValue(Math.abs(windDiff), windSpeedUnit, 1)}`);
    } else {
      trendParts.push('wind holding steady');
    }
  }

  if (pressureValues.length >= 2) {
    const pressureDiff = pressureValues[pressureValues.length - 1] - pressureValues[0];

    if (Math.abs(pressureDiff) >= PRESSURE_TREND_THRESHOLD_HPA) {
      trendParts.push(`pressure ${pressureDiff > 0 ? 'rising' : 'falling'} by ${formatPressureValue(Math.abs(pressureDiff), pressureUnit, 1)}`);
    }
  }

  return trendParts.length ? `Trend lines show ${joinSummaryParts(trendParts)}.` : '';
}

function formatRainHighlight(rainSummary) {
  if (!rainSummary) {
    return '';
  }

  if (rainSummary === 'no rain detected') {
    return 'None detected';
  }

  if (rainSummary === 'rain detected in sampled intervals') {
    return 'Detected in samples';
  }

  return `${rainSummary.replace(/^rain total /, '')} today`;
}

function formatWindHighlight(windSummary) {
  if (!windSummary) {
    return '';
  }

  if (windSummary === 'wind has stayed light') {
    return 'Light';
  }

  return capitalizeFirst(
    windSummary
      .replace(' with peak gusts near ', ', gusts ')
      .replace(' with sustained wind near ', ', sustained '),
  );
}

function formatTrendHighlight(trendSummary) {
  return trendSummary
    ? trendSummary.replace(/^Trend lines show /, 'Trends: ')
    : '';
}

export function buildDailyWeatherSummaryDetails(historyItems, {
  observationTimeMs,
  precipitationUnit = 'mm',
  pressureUnit = 'hpa',
  temperatureUnit = 'c',
  weatherBlock,
  windSpeedUnit = 'ms',
} = {}) {
  const historyPoints = (historyItems || [])
    .map((item) => {
      const weather = getHistoryWeatherBlock(item);
      const observedAtMs = getHistoryObservationTimeMs(item);

      return weather && observedAtMs ? { observedAtMs, weather } : null;
    })
    .filter(Boolean);
  const latestPoint = weatherBlock && Number.isFinite(observationTimeMs)
    ? { observedAtMs: observationTimeMs, weather: weatherBlock }
    : null;
  const allPoints = latestPoint
    ? [...historyPoints, latestPoint]
    : historyPoints;
  const targetDateKey =
    getStationDateKey(observationTimeMs) ||
    getStationDateKey(allPoints[allPoints.length - 1]?.observedAtMs);

  if (!targetDateKey) {
    return null;
  }

  const points = allPoints
    .filter((point) => getStationDateKey(point.observedAtMs) === targetDateKey)
    .sort((left, right) => left.observedAtMs - right.observedAtMs);

  if (!points.length) {
    return null;
  }

  const temperatureValues = points
    .map((point) => getFirstNumericWeatherValue(point.weather, ['air_temp_c', 'temp_c']))
    .filter((value) => value !== null);
  const highTemp = temperatureValues.length ? Math.max(...temperatureValues) : null;
  const lowTemp = temperatureValues.length ? Math.min(...temperatureValues) : null;
  const temperatureSummary = highTemp !== null && lowTemp !== null
    ? `temperature high ${formatTemperatureValue(highTemp, temperatureUnit, 1)}, low ${formatTemperatureValue(lowTemp, temperatureUnit, 1)}`
    : '';
  const temperatureHighlight = highTemp !== null && lowTemp !== null
    ? `High ${formatTemperatureValue(highTemp, temperatureUnit, 1)} / low ${formatTemperatureValue(lowTemp, temperatureUnit, 1)}`
    : '';
  const weatherEvent = describeWeatherEvent(points);
  const skyCoverSummary = describeSkyCover(points);
  const skySummary =
    weatherEvent &&
    (
      weatherEvent.key === 'thunderstorm' ||
      weatherEvent.key === 'lightning' ||
      !skyCoverSummary ||
      skyCoverSummary !== 'mostly cloudy'
    )
      ? weatherEvent.skySummary
      : skyCoverSummary;
  const rainSummary = describeRain(points, precipitationUnit);
  const windSummary = describeWind(points, windSpeedUnit, weatherEvent);
  const summaryParts = [
    temperatureSummary,
    skySummary,
    rainSummary,
    windSummary,
  ];
  const summary = summaryParts.filter(Boolean).join('; ');
  const trendSummary = describeTrend(points, temperatureUnit, windSpeedUnit, pressureUnit);

  if (!summary && !trendSummary) {
    return null;
  }

  return {
    items: [
      { key: 'temperature', label: 'Temp', value: temperatureHighlight },
      { key: 'sky', label: 'Sky', value: capitalizeFirst(skySummary) },
      { key: 'rain', label: 'Rain', value: formatRainHighlight(rainSummary) },
      { key: 'wind', label: 'Wind', value: formatWindHighlight(windSummary) },
    ].filter((item) => item.value),
    summaryText: `${summary ? `Today so far: ${summary}.` : 'Today so far:'}${trendSummary ? ` ${trendSummary}` : ''}`,
    trendText: formatTrendHighlight(trendSummary),
  };
}

export function buildDailyWeatherSummary(historyItems, options = {}) {
  return buildDailyWeatherSummaryDetails(historyItems, options)?.summaryText || '';
}

function getDailySummaryIconName(key) {
  switch (key) {
    case 'temperature':
      return 'temperature';
    case 'sky':
      return 'sky';
    case 'rain':
      return 'rain';
    case 'wind':
      return 'wind';
    default:
      return 'details';
  }
}

export function getHealthState(arrivalTimeMs, nowMs = Date.now(), isInitialLoad = false) {
  if (isInitialLoad) {
    return {
      tone: 'unknown',
      label: 'Loading',
      note: 'Fetching the first station reading.',
    };
  }

  if (!Number.isFinite(arrivalTimeMs)) {
    return {
      tone: 'critical',
      label: 'Offline',
      note: 'No station reading is available yet.',
    };
  }

  const readingAgeMs = Math.max(0, nowMs - arrivalTimeMs);

  if (readingAgeMs <= HEALTHY_READING_MAX_AGE_MS) {
    return {
      tone: 'healthy',
      label: 'Online',
      note: 'Latest reading is within the last 10 minutes.',
    };
  }

  if (readingAgeMs <= WARNING_READING_MAX_AGE_MS) {
    return {
      tone: 'warning',
      label: 'Delayed signal',
      note: 'Latest reading is between 10 minutes and 1 hour old.',
    };
  }

  return {
    tone: 'critical',
    label: 'Offline',
    note: 'No reading has arrived within the last hour.',
  };
}

function getBatteryVoltageState(value) {
  const numericValue = toNumber(value);

  if (numericValue === null) {
    return {
      tone: 'unknown',
      label: 'Battery n/a',
      note: 'Battery voltage is not reported.',
    };
  }

  if (numericValue >= 2.55) {
    return {
      tone: 'healthy',
      label: 'Battery good',
      note: 'Battery voltage is in the normal operating band.',
    };
  }

  if (numericValue >= 2.35) {
    return {
      tone: 'warning',
      label: 'Battery watch',
      note: 'Battery voltage is below the usual full range but not critical.',
    };
  }

  return {
    tone: 'critical',
    label: 'Low battery',
    note: 'Battery voltage is below the low-voltage threshold.',
  };
}

function getMetricState(metric, value, weatherBlock) {
  const numericValue = toNumber(value);

  switch (metric.key) {
    case 'air_temp_c':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Temperature not reported');
      if (numericValue < 5) return makeMetricState('bad', 'Cold', 'Well below the comfort range');
      if (numericValue < 18) return makeMetricState('warn', 'Cool', 'A bit chilly');
      if (numericValue <= 26) return makeMetricState('ideal', 'Comfortable', 'Mild operating conditions');
      if (numericValue <= 32) return makeMetricState('warn', 'Warm', 'Heat is building');
      return makeMetricState('bad', 'Hot', 'Heat stress conditions');
    case 'relative_humidity_pct':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Humidity not reported');
      if (numericValue < 30) return makeMetricState('bad', 'Too Dry', 'Air is very dry');
      if (numericValue < 40) return makeMetricState('warn', 'Dry', 'Below ideal humidity');
      if (numericValue <= 60) return makeMetricState('ideal', 'Balanced', 'Comfort humidity band');
      if (numericValue <= 70) return makeMetricState('warn', 'Humid', 'Moisture is rising');
      return makeMetricState('bad', 'Too Humid', 'Air feels heavy');
    case 'station_pressure_hpa':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Pressure not reported');
      if (numericValue < 995) return makeMetricState('bad', 'Low', 'Stormy pressure signature');
      if (numericValue < 1003) return makeMetricState('warn', 'Soft', 'Below steady pressure');
      if (numericValue <= 1024) return makeMetricState('ideal', 'Stable', 'Typical fair-weather band');
      if (numericValue <= 1030) return makeMetricState('warn', 'High', 'Above the steady band');
      return makeMetricState('bad', 'Very High', 'Unusually high pressure');
    case 'wind_avg_ms':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Average wind not reported');
      if (numericValue < 4) return makeMetricState('ideal', 'Calm', 'Light sustained wind');
      if (numericValue < 8) return makeMetricState('warn', 'Breezy', 'Wind is noticeable');
      return makeMetricState('bad', 'Windy', 'Sustained wind is elevated');
    case 'wind_gust_ms':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Gust data not reported');
      if (numericValue < 7) return makeMetricState('ideal', 'Steady', 'No strong gust spikes');
      if (numericValue < 12) return makeMetricState('warn', 'Gusty', 'Wind bursts are present');
      return makeMetricState('bad', 'Strong Gusts', 'Sharp gust spikes');
    case 'wind_dir_deg':
      return makeMetricState('info', degreesToCardinal(value), 'Wind direction');
    case 'rain_interval_mm':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Rain interval not reported');
      if (numericValue === 0) return makeMetricState('ideal', 'Dry Now', 'No rain in the latest interval');
      if (numericValue < 2) return makeMetricState('warn', 'Light Rain', 'Some rainfall detected');
      return makeMetricState('bad', 'Heavy Rain', 'Current rainfall is significant');
    case 'local_day_rain_mm':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Daily rain total not reported');
      if (numericValue < 2) return makeMetricState('ideal', 'Dry Day', 'Low accumulation today');
      if (numericValue < 10) return makeMetricState('warn', 'Wet Day', 'Rain has built up today');
      return makeMetricState('bad', 'Soaked', 'High daily rain total');
    case 'solar_radiation_wm2':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Solar data not reported');
      if (numericValue < 100) return makeMetricState('info', 'Low Sun', 'Low solar input');
      if (numericValue < 700) return makeMetricState('ideal', 'Moderate Sun', 'Healthy daylight levels');
      if (numericValue < 900) return makeMetricState('warn', 'Strong Sun', 'High solar load');
      return makeMetricState('bad', 'Intense Sun', 'Very strong solar load');
    case 'uv_index':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'UV not reported');
      if (numericValue <= 2) return makeMetricState('ideal', 'Low', 'Low UV exposure');
      if (numericValue <= 5) return makeMetricState('warn', 'Moderate', 'Use some UV caution');
      return makeMetricState('bad', 'High', 'Elevated UV exposure');
    case 'battery_voltage_v':
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Battery voltage not reported');
      if (numericValue >= 2.55) return makeMetricState('ideal', 'Healthy', 'Battery level is in the normal operating band');
      if (numericValue >= 2.35) return makeMetricState('warn', 'Watch', 'Battery is below the usual full range');
      return makeMetricState('bad', 'Low Battery', 'Battery reserve is weak');
    case 'lightning_strike_count': {
      if (numericValue === null) return makeMetricState('muted', 'No Data', 'Lightning data not reported');
      const strikeDistance = toNumber(weatherBlock?.lightning_avg_distance_km);
      if (numericValue === 0) return makeMetricState('ideal', 'Clear', 'No lightning activity detected');
      if (strikeDistance !== null && strikeDistance < 10) {
        return makeMetricState('bad', 'Nearby', 'Lightning is close to the station');
      }
      if (strikeDistance !== null && strikeDistance < 25) {
        return makeMetricState('warn', 'Regional', 'Lightning is in the area');
      }
      return makeMetricState('bad', 'Detected', 'Lightning activity reported');
    }
    default:
      return makeMetricState('info', 'Sensor', 'Live weather reading');
  }
}

function WeatherPanel({
  id,
  title,
  icon = 'details',
  className = '',
  children,
  collapsible = false,
  defaultOpen = true,
  isOpen: controlledIsOpen,
  onOpenChange,
}) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(() => (
    collapsible ? defaultOpen : true
  ));
  const isControlled = typeof controlledIsOpen === 'boolean';
  const contentId = useMemo(
    () => `weather-panel-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    [title],
  );
  const isExpanded = collapsible
    ? (isControlled ? controlledIsOpen : uncontrolledIsOpen)
    : true;
  const handleToggle = () => {
    const nextOpen = !isExpanded;

    if (!isControlled) {
      setUncontrolledIsOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  };

  return (
    <section id={id} className={`weather-panel ${className}`.trim()}>
      <div className="weather-panel__body">
        {collapsible ? (
          <button
            type="button"
            className="weather-panel-toggle"
            onClick={handleToggle}
            aria-expanded={isExpanded}
            aria-controls={contentId}
          >
            <span className="weather-panel-toggle__lead">
              <WeatherUiIcon name={icon} className="weather-panel-toggle__glyph" />
              <span className="weather-panel-toggle__title">{title}</span>
            </span>
            <span className="weather-panel-toggle__meta">
              <span className="weather-panel-toggle__icon">
                <WeatherUiIcon name={isExpanded ? 'chevron-up' : 'chevron-down'} />
              </span>
            </span>
          </button>
        ) : (
          <h3 className="weather-panel__title">
            <WeatherUiIcon name={icon} className="weather-panel__title-icon" />
            {title}
          </h3>
        )}
        <div id={contentId} className="weather-panel__content" hidden={!isExpanded}>
          {children}
        </div>
      </div>
    </section>
  );
}

function MeshtasticWeatherStation() {
  const [observation, setObservation] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [dailyHistoryItems, setDailyHistoryItems] = useState([]);
  const [sunTimes, setSunTimes] = useState(null);
  const [dailyHistoryLoading, setDailyHistoryLoading] = useState(true);
  const [dailyHistoryLoaded, setDailyHistoryLoaded] = useState(false);
  const [historyMeta, setHistoryMeta] = useState(EMPTY_HISTORY_META);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [historyRainTotal, setHistoryRainTotal] = useState(EMPTY_HISTORY_RAIN_TOTAL);
  const [historyBackendSummary, setHistoryBackendSummary] = useState({
    cacheKey: '',
    status: 'pending',
    summary: null,
  });
  const [historyCsvExport, setHistoryCsvExport] = useState({
    error: '',
    isExporting: false,
    message: '',
  });
  const [selectedHistoryWindowKey, setSelectedHistoryWindowKey] = useState('24h');
  const [customHistoryWindowRange, setCustomHistoryWindowRange] = useState(null);
  const [unitSystem, setUnitSystem] = useState('imperial');
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [readingsPanelOpen, setReadingsPanelOpen] = useState(false);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const historyChartContainerRef = useRef(null);
  const historyCacheRef = useRef(new Map());
  const dailyHistoryCacheRef = useRef(new Map());
  const sunTimesCacheRef = useRef(new Map());
  const historyExportControllerRef = useRef(null);
  const lastHistoryRefreshKeyRef = useRef(0);
  const lastDailyHistoryRefreshKeyRef = useRef(0);
  const [historyChartWidth, setHistoryChartWidth] = useState(null);

  const hasConfig = Boolean(WEATHER_STATION_URL && WEATHER_STATION_API_KEY);
  const hasHistoryConfig = Boolean(WEATHER_STATION_HISTORY_URL && WEATHER_STATION_API_KEY);
  const missingConfigKeys = [
    !WEATHER_STATION_URL ? 'REACT_APP_WEATHER_STATION_URL' : null,
    !WEATHER_STATION_HISTORY_URL ? 'REACT_APP_WEATHER_STATION_HISTORY_URL' : null,
    !WEATHER_STATION_API_KEY ? 'REACT_APP_WEATHER_STATION_API_KEY' : null,
  ].filter(Boolean);
  const selectedHistoryWindow = useMemo(
    () => getSelectedHistoryWindowDetails(selectedHistoryWindowKey, customHistoryWindowRange),
    [customHistoryWindowRange, selectedHistoryWindowKey],
  );
  const activeUnitPreset = UNIT_SYSTEM_PRESETS[unitSystem] || UNIT_SYSTEM_PRESETS.imperial;
  const {
    temperatureUnit,
    windSpeedUnit,
    precipitationUnit,
    pressureUnit,
    distanceUnit,
  } = activeUnitPreset;
  const latestItem = observation?.item && isPlainObject(observation.item) ? observation.item : null;
  const weatherBlock =
    latestItem?.weather && isPlainObject(latestItem.weather) ? latestItem.weather : null;
  const historyStationId =
    latestItem?.source_node_id ||
    latestItem?.pk?.replace(/^STATION#/, '') ||
    getStationIdFromRequestUrl(WEATHER_STATION_HISTORY_URL) ||
    '';
  const historySampleSize = getHistorySampleSize(historyChartWidth);
  const clearHistoryCsvExportStatus = () => {
    historyExportControllerRef.current?.abort();
    historyExportControllerRef.current = null;
    setHistoryCsvExport({
      error: '',
      isExporting: false,
      message: '',
    });
  };
  const applyCustomHistoryWindowRange = (range) => {
    const normalizedRange = normalizeHistoryWindowRange(range);

    if (!normalizedRange) {
      return false;
    }

    clearHistoryCsvExportStatus();
    setCustomHistoryWindowRange(normalizedRange);
    setSelectedHistoryWindowKey(CUSTOM_HISTORY_WINDOW_KEY);
    return true;
  };
  const handleHistoryWindowChange = (nextWindowKey) => {
    clearHistoryCsvExportStatus();

    if (nextWindowKey === CUSTOM_HISTORY_WINDOW_KEY) {
      setCustomHistoryWindowRange((currentRange) => (
        normalizeHistoryWindowRange(currentRange) ||
        getDefaultCustomHistoryWindowRange()
      ));
      setSelectedHistoryWindowKey(CUSTOM_HISTORY_WINDOW_KEY);
      return;
    }

    setSelectedHistoryWindowKey(nextWindowKey);
  };
  const handleHistoryCsvExport = async () => {
    if (historyCsvExport.isExporting) {
      return;
    }

    if (!hasHistoryConfig) {
      setHistoryCsvExport({
        error: 'Missing weather history env configuration.',
        isExporting: false,
        message: '',
      });
      return;
    }

    if (!historyStationId) {
      setHistoryCsvExport({
        error: 'Weather history station ID is missing.',
        isExporting: false,
        message: '',
      });
      return;
    }

    historyExportControllerRef.current?.abort();

    const controller = new AbortController();
    const { fromDate, toDate } = getHistoryWindowDateRange(selectedHistoryWindow);
    const fromIso = formatUtcIso(fromDate);
    const toIso = formatUtcIso(toDate);

    historyExportControllerRef.current = controller;
    setHistoryCsvExport({
      error: '',
      isExporting: true,
      message: '',
    });

    try {
      const exportResult = await requestWeatherHistoryExport({
        historyUrl: WEATHER_STATION_HISTORY_URL,
        apiKey: WEATHER_STATION_API_KEY,
        stationId: historyStationId,
        from: fromIso,
        signal: controller.signal,
        to: toIso,
        onProgress: (progress) => {
          if (controller.signal.aborted) return;
          setHistoryCsvExport({
            error: '',
            isExporting: true,
            message: `Preparing export · ${Number(progress.rowsExported || 0).toLocaleString()} readings · metric units / UTC`,
          });
        },
      });
      if (controller.signal.aborted) return;
      downloadWeatherExport(exportResult);
      setHistoryCsvExport({
        error: '',
        isExporting: false,
        message: `Export ready · ${Number(exportResult.rowsExported || 0).toLocaleString()} readings · metric units / UTC`,
        downloadUrl: exportResult.downloadUrl,
        fileName: exportResult.fileName,
      });
    } catch (exportError) {
      if (exportError.name === 'AbortError') {
        return;
      }

      setHistoryCsvExport({
        error: exportError.message || 'Unable to export weather history.',
        isExporting: false,
        message: '',
      });
    } finally {
      if (historyExportControllerRef.current === controller) {
        historyExportControllerRef.current = null;
      }
    }
  };
  const handleChartCsvExport = () => {
    if (!historyItems.length || historyLoading || historyError) return;
    const fallbackRange = getHistoryWindowDateRange(selectedHistoryWindow);
    const fromDate = historyMeta.from ? new Date(historyMeta.from) : fallbackRange.fromDate;
    const toDate = historyMeta.to ? new Date(historyMeta.to) : fallbackRange.toDate;
    const filename = buildWeatherHistoryCsvFilename(fromDate, toDate).replace('.csv', `-${historyMeta.resolution || 'raw'}-chart.csv`);
    downloadCsvFile(buildChartHistoryCsv(historyItems, historyMeta), filename);
  };
  const flattenedObservation = useMemo(
    () => (observation && !weatherBlock ? flattenPayload(observation) : []),
    [observation, weatherBlock],
  );
  const fallbackSummaryItems = useMemo(() => SUMMARY_FIELDS
    .map((field) => {
      const match = findFirstMatchingEntry(flattenedObservation, field.patterns);
      if (!match) {
        return null;
      }

      return {
        label: field.label,
        value:
          field.label === 'Temperature'
            ? formatTemperatureValue(match.value, temperatureUnit, 1)
            : field.label === 'Wind Speed'
              ? formatWindSpeedValue(match.value, windSpeedUnit, 1)
              : field.label === 'Rainfall'
                ? formatPrecipitationValue(match.value, precipitationUnit, 1)
                : formatValue(match.value),
        tone: 'info',
        status: 'Matched',
        note: humanizePath(match.path),
      };
    })
    .filter(Boolean), [
    flattenedObservation,
    precipitationUnit,
    temperatureUnit,
    windSpeedUnit,
  ]);
  const weatherSummaryItems = useMemo(() => (
    weatherBlock
      ? WEATHER_METRICS
        .filter((metric) => metric.featured)
        .map((metric) => {
          if (!(metric.key in weatherBlock)) {
            return null;
          }

          const state = getMetricState(metric, weatherBlock[metric.key], weatherBlock);
          return {
            label: metric.label,
            value: formatWeatherMetric(
              metric,
              weatherBlock[metric.key],
              temperatureUnit,
              windSpeedUnit,
              precipitationUnit,
              pressureUnit,
              distanceUnit,
            ),
            tone: state.tone,
            status: state.status,
            note: state.note,
          };
        })
        .filter(Boolean)
      : fallbackSummaryItems
  ), [
    fallbackSummaryItems,
    distanceUnit,
    precipitationUnit,
    pressureUnit,
    temperatureUnit,
    weatherBlock,
    windSpeedUnit,
  ]);
  const weatherDetailItems = useMemo(() => (
    weatherBlock
      ? [
        ...WEATHER_METRICS
          .map((metric) => {
            if (!(metric.key in weatherBlock)) {
              return null;
            }

            return {
              label: metric.label,
              path: `item.weather.${metric.key}`,
              value: formatWeatherMetric(
                metric,
                weatherBlock[metric.key],
                temperatureUnit,
                windSpeedUnit,
                precipitationUnit,
                pressureUnit,
                distanceUnit,
              ),
            };
          })
          .filter(Boolean),
        ...Object.entries(weatherBlock)
          .filter(([key]) => !WEATHER_METRIC_KEYS.has(key))
          .map(([key, value]) => ({
            label: humanizePath(key),
            path: `item.weather.${key}`,
            value: formatValue(value),
          })),
      ]
      : []
  ), [
    distanceUnit,
    precipitationUnit,
    pressureUnit,
    temperatureUnit,
    weatherBlock,
    windSpeedUnit,
  ]);
  const quickTempValue = useMemo(() => (
    weatherBlock && 'air_temp_c' in weatherBlock
      ? formatTemperatureValue(weatherBlock.air_temp_c, temperatureUnit, 1)
      : 'n/a'
  ), [temperatureUnit, weatherBlock]);
  const quickFeelsLikeValue = useMemo(() => (
    weatherBlock && 'air_temp_c' in weatherBlock
      ? formatFeelsLikeTemperatureValue(
        weatherBlock.air_temp_c,
        weatherBlock.relative_humidity_pct,
        weatherBlock.wind_avg_ms,
        temperatureUnit,
        1,
      )
      : 'n/a'
  ), [temperatureUnit, weatherBlock]);
  const quickHumidityValue = useMemo(() => (
    weatherBlock && 'relative_humidity_pct' in weatherBlock
      ? formatMetricValue(weatherBlock.relative_humidity_pct, '%', 0)
      : 'n/a'
  ), [weatherBlock]);
  const quickWindValue = useMemo(() => (
    weatherBlock && 'wind_avg_ms' in weatherBlock
      ? formatWindSpeedValue(weatherBlock.wind_avg_ms, windSpeedUnit, 1)
      : 'n/a'
  ), [weatherBlock, windSpeedUnit]);
  const quickPressureValue = useMemo(() => (
    weatherBlock && 'station_pressure_hpa' in weatherBlock
      ? formatPressureValue(weatherBlock.station_pressure_hpa, pressureUnit, 1)
      : 'n/a'
  ), [pressureUnit, weatherBlock]);
  const quickBatteryVoltageValue = useMemo(() => (
    weatherBlock && 'battery_voltage_v' in weatherBlock
      ? formatMetricValue(weatherBlock.battery_voltage_v, ' V', 2)
      : 'n/a'
  ), [weatherBlock]);
  const stationReadingTimeMs = useMemo(
    () => getObservationDisplayTimestampMs(latestItem) || null,
    [latestItem],
  );
  const latestArrivalTimeMs = useMemo(
    () => getObservationArrivalTimestampMs(latestItem) || null,
    [latestItem],
  );
  const weatherHealth = useMemo(
    () => getHealthState(latestArrivalTimeMs, currentTime.getTime(), loading && !latestItem),
    [currentTime, latestArrivalTimeMs, latestItem, loading],
  );
  const batteryVoltageState = useMemo(
    () => getBatteryVoltageState(weatherBlock?.battery_voltage_v),
    [weatherBlock],
  );
  const latestReadingDetails = useMemo(
    () => getLatestReadingDetails(stationReadingTimeMs, currentTime.getTime()),
    [currentTime, stationReadingTimeMs],
  );
  const sunTimesDateKey = stationReadingTimeMs ? getStationDateKey(stationReadingTimeMs) : '';
  const weatherSummaryBlock = useMemo(() => {
    if (!weatherBlock || !sunTimes) {
      return weatherBlock;
    }

    return {
      ...weatherBlock,
      sunset_utc: sunTimes.sunsetUtc,
      civil_twilight_end_utc: sunTimes.civilTwilightEndUtc,
    };
  }, [sunTimes, weatherBlock]);
  const weatherSummaryTimestamp = stationReadingTimeMs ?? weatherBlock?.timestamp;
  const weatherSummary = useMemo(
    () => resolveWeatherSummary(weatherSummaryBlock, weatherSummaryTimestamp),
    [weatherSummaryBlock, weatherSummaryTimestamp],
  );
  const moonPhase = useMemo(
    () => getMoonPhaseDetails(weatherSummaryTimestamp),
    [weatherSummaryTimestamp],
  );
  const civilTwilightDetails = useMemo(
    () => getCivilTwilightDetails(weatherSummaryBlock, weatherSummaryTimestamp),
    [weatherSummaryBlock, weatherSummaryTimestamp],
  );
  const civilTwilightTimingText = useMemo(() => {
    if (
      !civilTwilightDetails.isActive ||
      civilTwilightDetails.sunsetTimestampMs === null ||
      civilTwilightDetails.civilTwilightEndTimestampMs === null
    ) {
      return '';
    }

    const sunsetLabel = formatStationTimeOfDay(civilTwilightDetails.sunsetTimestampMs);
    const civilTwilightEndLabel =
      formatStationTimeOfDay(civilTwilightDetails.civilTwilightEndTimestampMs);

    if (!sunsetLabel || !civilTwilightEndLabel) {
      return '';
    }

    return `Today's sunset is ${sunsetLabel}. Civil twilight ends at ${civilTwilightEndLabel}.`;
  }, [
    civilTwilightDetails.civilTwilightEndTimestampMs,
    civilTwilightDetails.isActive,
    civilTwilightDetails.sunsetTimestampMs,
  ]);
  const dailyWeatherSummary = useMemo(() => {
    if (!hasHistoryConfig || !dailyHistoryLoaded) {
      return null;
    }

    return buildDailyWeatherSummaryDetails(dailyHistoryItems, {
      observationTimeMs: stationReadingTimeMs,
      precipitationUnit,
      pressureUnit,
      temperatureUnit,
      weatherBlock,
      windSpeedUnit,
    });
  }, [
    dailyHistoryItems,
    dailyHistoryLoaded,
    hasHistoryConfig,
    precipitationUnit,
    pressureUnit,
    stationReadingTimeMs,
    temperatureUnit,
    weatherBlock,
    windSpeedUnit,
  ]);
  const historyRainTotalValue = useMemo(() => {
    const valueMm = historyRainTotal.valueMm;

    return Number.isFinite(valueMm)
      ? convertPrecipitationValue(valueMm, precipitationUnit)
      : null;
  }, [historyRainTotal.valueMm, precipitationUnit]);
  const isDailyWeatherSummaryLoading = hasHistoryConfig && dailyHistoryLoading && !dailyWeatherSummary;
  const baseWeatherSummaryTitle = weatherSummary.condition.includes('night')
    ? `${weatherSummary.title} Current moon phase: ${moonPhase.label}.`
    : weatherSummary.title;
  const visibleWeatherSummaryTitle = [
    baseWeatherSummaryTitle,
    civilTwilightTimingText,
  ].filter(Boolean).join(' ');
  const refreshInProgress = loading || historyLoading || dailyHistoryLoading;
  const visibleWeatherSummaryLabel = weatherSummary.condition.includes('night')
    ? `${weatherSummary.label} - ${moonPhase.label}`
    : weatherSummary.label;

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => () => {
    historyExportControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const container = historyChartContainerRef.current;
    if (!container) {
      return undefined;
    }

    const updateWidth = (nextWidth) => {
      const measuredWidth = Number.isFinite(nextWidth)
        ? nextWidth
        : container.getBoundingClientRect().width;
      const roundedWidth = Math.round(measuredWidth);

      if (!Number.isFinite(roundedWidth) || roundedWidth <= 0) {
        return;
      }

      setHistoryChartWidth((currentWidth) => (
        currentWidth === roundedWidth ? currentWidth : roundedWidth
      ));
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      const handleResize = () => updateWidth();
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }

    const observer = new ResizeObserver((entries) => {
      updateWidth(entries[0]?.contentRect?.width);
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!hasConfig) {
      setLoading(false);
      setError('Missing weather station env configuration.');
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();

    const loadLatestObservation = async (showSpinner) => {
      if (showSpinner && isMounted) {
        setLoading(true);
      }

      if (isMounted) {
        setError('');
      }

      try {
        const response = await fetch(WEATHER_STATION_URL, {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            'x-weatherstation-key': WEATHER_STATION_API_KEY,
          },
          signal: controller.signal,
        });

        const contentType = response.headers.get('content-type') || '';
        const payload = contentType.includes('application/json')
          ? await response.json()
          : await response.text();

        if (!response.ok) {
          const message = getResponseErrorMessage(
            payload,
            `Weather request failed with status ${response.status}.`,
          );
          throw new Error(message);
        }

        if (!isMounted) {
          return;
        }

        setObservation(
          typeof payload === 'object' && payload !== null ? payload : { response: payload },
        );
      } catch (fetchError) {
        if (fetchError.name === 'AbortError' || !isMounted) {
          return;
        }

        setError(fetchError.message || 'Unable to load weather station data.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadLatestObservation(true);
    const intervalId = window.setInterval(() => {
      loadLatestObservation(false);
    }, AUTO_REFRESH_MS);

    return () => {
      isMounted = false;
      controller.abort();
      window.clearInterval(intervalId);
    };
  }, [hasConfig, refreshKey]);

  useEffect(() => {
    if (!sunTimesDateKey) {
      setSunTimes(null);
      return undefined;
    }

    const cachedEntry = sunTimesCacheRef.current.get(sunTimesDateKey);

    if (cachedEntry) {
      setSunTimes(cachedEntry);
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();

    const loadSunTimes = async () => {
      try {
        const response = await fetch(buildSunTimesRequestUrl(sunTimesDateKey), {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
          signal: controller.signal,
        });
        const payload = await response.json();

        if (!response.ok || payload?.status !== 'OK') {
          return;
        }

        const nextSunTimes = normalizeSunTimesPayload(payload);

        if (!nextSunTimes || !isMounted) {
          return;
        }

        sunTimesCacheRef.current.set(sunTimesDateKey, nextSunTimes);
        trimHistoryCache(sunTimesCacheRef.current);
        setSunTimes(nextSunTimes);
      } catch (fetchError) {
        if (fetchError.name !== 'AbortError' && isMounted) {
          setSunTimes(null);
        }
      }
    };

    setSunTimes(null);
    loadSunTimes();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [refreshKey, sunTimesDateKey]);

  useEffect(() => {
    if (!hasHistoryConfig) {
      setHistoryItems([]);
      setHistoryMeta(EMPTY_HISTORY_META);
      setHistoryLoading(false);
      setHistoryError('Missing weather history env configuration.');
      return undefined;
    }

    if (!historyStationId) {
      setHistoryItems([]);
      setHistoryMeta(EMPTY_HISTORY_META);
      setHistoryLoading(false);
      setHistoryError('Weather history station ID is missing.');
      return undefined;
    }

    if (!historySampleSize) {
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();
    const shouldBypassCache = refreshKey !== lastHistoryRefreshKeyRef.current;
    lastHistoryRefreshKeyRef.current = refreshKey;
    const { fromDate, toDate } = getHistoryWindowDateRange(selectedHistoryWindow);
    const fromIso = formatUtcIso(fromDate);
    const toIso = formatUtcIso(toDate);
    const cacheKey = buildHistoryCacheKey(
      historyStationId,
      fromIso,
      toIso,
      `chart-v1-display-${historySampleSize}`,
    );
    const backendSummaryCacheKey = buildHistoryCacheKey(
      historyStationId,
      fromIso,
      toIso,
      'backend-summary-v1',
    );
    const cacheStaleMs = getHistoryCacheStaleMs(selectedHistoryWindow.hours);
    const cachedEntry = historyCacheRef.current.get(cacheKey);

    if (
      !shouldBypassCache &&
      cachedEntry &&
      (Date.now() - cachedEntry.cachedAt) < cacheStaleMs
    ) {
      setHistoryItems(cachedEntry.items);
      setHistoryMeta(cachedEntry.meta);
      setHistoryBackendSummary({
        cacheKey: backendSummaryCacheKey,
        status: cachedEntry.summary ? 'available' : 'unavailable',
        summary: cachedEntry.summary || null,
      });
      setHistoryLoading(false);
      setHistoryError('');

      return () => {
        isMounted = false;
        controller.abort();
      };
    }

    const debounceId = window.setTimeout(async () => {
      try {
        const requestUrl = buildChartHistoryUrl(WEATHER_STATION_HISTORY_URL, {
          stationId: historyStationId,
          fromIso,
          toIso,
          maxPoints: historySampleSize,
        });
        const response = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            'x-weatherstation-key': WEATHER_STATION_API_KEY,
          },
          signal: controller.signal,
        });

        const contentType = response.headers.get('content-type') || '';
        const payload = contentType.includes('application/json')
          ? await response.json()
          : await response.text();

        if (!response.ok) {
          const message = getResponseErrorMessage(
            payload,
            `Weather history request failed with status ${response.status}.`,
          );
          throw new Error(message);
        }

        if (payload?.ok === false) {
          throw new Error(
            getResponseErrorMessage(payload, 'Weather history response returned ok=false.'),
          );
        }

        if (!isMounted) {
          return;
        }

        const normalizedItems = dedupeHistoryItems(
          Array.isArray(payload?.items) ? payload.items : [],
        ).sort(
          (left, right) => getHistoryObservationTimeMs(left) - getHistoryObservationTimeMs(right),
        );
        const nextHistoryMeta = getChartHistoryMeta(payload);
        const backendSummary = isPlainObject(payload?.summary)
          ? payload.summary
          : null;

        historyCacheRef.current.set(cacheKey, {
          cachedAt: Date.now(),
          items: normalizedItems,
          meta: nextHistoryMeta,
          summary: backendSummary,
        });
        trimHistoryCache(historyCacheRef.current);

        setHistoryItems(normalizedItems);
        setHistoryMeta(nextHistoryMeta);
        setHistoryBackendSummary({
          cacheKey: backendSummaryCacheKey,
          status: backendSummary ? 'available' : 'unavailable',
          summary: backendSummary,
        });
      } catch (fetchError) {
        if (fetchError.name === 'AbortError' || !isMounted) {
          return;
        }

        setHistoryItems([]);
        setHistoryMeta(EMPTY_HISTORY_META);
        setHistoryError(fetchError.message || 'Unable to load weather history.');
        setHistoryBackendSummary({
          cacheKey: backendSummaryCacheKey,
          status: 'unavailable',
          summary: null,
        });
      } finally {
        if (isMounted) {
          setHistoryLoading(false);
        }
      }
    }, HISTORY_REQUEST_DEBOUNCE_MS);

    setHistoryLoading(true);
    setHistoryError('');
    setHistoryBackendSummary({
      cacheKey: backendSummaryCacheKey,
      status: 'pending',
      summary: null,
    });

    return () => {
      isMounted = false;
      controller.abort();
      window.clearTimeout(debounceId);
    };
  }, [
    hasHistoryConfig,
    historySampleSize,
    historyStationId,
    refreshKey,
    selectedHistoryWindow,
  ]);

  useEffect(() => {
    const rain = historyBackendSummary.status === 'available' ? historyBackendSummary.summary?.rain : null;
    setHistoryRainTotal({
      isLoading: historyBackendSummary.status === 'pending',
      rainEventItems: [],
      valueMm: rain?.totalMm == null ? null : toNumber(rain.totalMm),
    });
  }, [historyBackendSummary]);
  useEffect(() => {
    if (!hasHistoryConfig) {
      setDailyHistoryItems([]);
      setDailyHistoryLoading(false);
      setDailyHistoryLoaded(false);
      return undefined;
    }

    if (!historyStationId || !stationReadingTimeMs) {
      setDailyHistoryItems([]);
      setDailyHistoryLoading(false);
      setDailyHistoryLoaded(false);
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();
    const shouldBypassCache = refreshKey !== lastDailyHistoryRefreshKeyRef.current;
    lastDailyHistoryRefreshKeyRef.current = refreshKey;
    const dailyHistoryWindow = getStationDayHistoryWindow(stationReadingTimeMs);

    if (!dailyHistoryWindow) {
      setDailyHistoryItems([]);
      setDailyHistoryLoading(false);
      setDailyHistoryLoaded(false);
      return undefined;
    }

    const fromIso = formatUtcIso(dailyHistoryWindow.fromDate);
    const toIso = formatUtcIso(dailyHistoryWindow.toDate);
    const cacheKey = buildHistoryCacheKey(
      historyStationId,
      fromIso,
      toIso,
      `daily-${DAILY_HISTORY_LIMIT}`,
    );
    const cachedEntry = dailyHistoryCacheRef.current.get(cacheKey);

    if (
      !shouldBypassCache &&
      cachedEntry &&
      (Date.now() - cachedEntry.cachedAt) < DAILY_HISTORY_CACHE_STALE_MS
    ) {
      setDailyHistoryItems(cachedEntry.items);
      setDailyHistoryLoading(false);
      setDailyHistoryLoaded(true);

      return () => {
        isMounted = false;
        controller.abort();
      };
    }

    const debounceId = window.setTimeout(async () => {
      try {
        const fetchedItems = [];
        const seenTokens = new Set();
        let nextToken = '';

        do {
          const requestUrl = buildHistoryRequestUrl(WEATHER_STATION_HISTORY_URL, {
            stationId: historyStationId,
            fromIso,
            toIso,
            sample: null,
            limit: DAILY_HISTORY_LIMIT,
            order: 'asc',
            nextToken,
          });
          const response = await fetch(requestUrl, {
            method: 'GET',
            headers: {
              'content-type': 'application/json',
              'x-weatherstation-key': WEATHER_STATION_API_KEY,
            },
            signal: controller.signal,
          });

          const contentType = response.headers.get('content-type') || '';
          const payload = contentType.includes('application/json')
            ? await response.json()
            : await response.text();

          if (!response.ok) {
            const message = getResponseErrorMessage(
              payload,
              `Daily weather history request failed with status ${response.status}.`,
            );
            throw new Error(message);
          }

          if (payload?.ok === false) {
            throw new Error(
              getResponseErrorMessage(payload, 'Daily weather history response returned ok=false.'),
            );
          }

          fetchedItems.push(...(Array.isArray(payload?.items) ? payload.items : []));

          const payloadNextToken = getHistoryResponseNextToken(payload);
          nextToken = payloadNextToken && !seenTokens.has(payloadNextToken)
            ? payloadNextToken
            : '';

          if (nextToken) {
            seenTokens.add(nextToken);
          }
        } while (nextToken);

        if (!isMounted) {
          return;
        }

        const normalizedItems = dedupeHistoryItems(
          fetchedItems,
        ).sort(
          (left, right) => getHistoryObservationTimeMs(left) - getHistoryObservationTimeMs(right),
        );

        dailyHistoryCacheRef.current.set(cacheKey, {
          cachedAt: Date.now(),
          items: normalizedItems,
        });
        trimHistoryCache(dailyHistoryCacheRef.current);

        setDailyHistoryItems(normalizedItems);
        setDailyHistoryLoaded(true);
      } catch (fetchError) {
        if (fetchError.name === 'AbortError' || !isMounted) {
          return;
        }

        setDailyHistoryItems([]);
        setDailyHistoryLoaded(false);
      } finally {
        if (isMounted) {
          setDailyHistoryLoading(false);
        }
      }
    }, HISTORY_REQUEST_DEBOUNCE_MS);

    setDailyHistoryLoading(true);
    setDailyHistoryLoaded(false);

    return () => {
      isMounted = false;
      controller.abort();
      window.clearTimeout(debounceId);
    };
  }, [
    hasHistoryConfig,
    historyStationId,
    refreshKey,
    stationReadingTimeMs,
  ]);

  return (
    <section className="weather-station-page">
      <div className="weather-station-shell">
        <aside className="weather-station-rail" aria-label="Weather station shortcuts">
          <a className="weather-rail-mark" href="#weather-station-top" aria-label="Weather station overview">
            <WeatherStationMark />
          </a>
          <nav className="weather-rail-nav" aria-label="Dashboard sections">
            <a className="weather-rail-link weather-rail-link--active" href="#weather-station-top" aria-label="Current conditions">
              <WeatherUiIcon name="sun" />
            </a>
            <a className="weather-rail-link" href="#weather-history" aria-label="Weather history">
              <WeatherUiIcon name="history" />
            </a>
            <a className="weather-rail-link" href="#weather-shared-data" aria-label="Shared weather data">
              <WeatherUiIcon name="globe" />
            </a>
            <a
              className="weather-rail-link"
              href="#weather-readings"
              aria-label="Latest weather readings"
              onClick={() => setReadingsPanelOpen(true)}
            >
              <WeatherUiIcon name="readings" />
            </a>
            <a
              className="weather-rail-link"
              href="#weather-details"
              aria-label="Sensor details"
              onClick={() => setDetailsPanelOpen(true)}
            >
              <WeatherUiIcon name="details" />
            </a>
            <a className="weather-rail-link" href="#weather-project" aria-label="Project build log">
              <WeatherUiIcon name="article" />
            </a>
          </nav>
          <span className="weather-rail-link weather-rail-link--status" aria-hidden="true">
            <WeatherUiIcon name="health" />
          </span>
        </aside>

        <div className="weather-station-main" id="weather-station-top">
          <header className="weather-station-topbar">
            <div className="weather-station-brand">
              <span className="weather-station-brand-mark" aria-hidden="true">
                <WeatherStationMark />
              </span>
              <div className="weather-station-brand-copy">
                <p className="weather-station-kicker">Garden Telemetry</p>
                <h2 className="weather-station-title">Emma&apos;s Court Weather Station</h2>
              </div>
            </div>
            <div className="weather-station-topbar-actions">
              <div className="weather-unit-toggle weather-unit-toggle--compact" role="group" aria-label="Reading units">
                <div className="weather-unit-buttons">
                  {Object.entries(UNIT_SYSTEM_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      type="button"
                      className={`weather-unit-button ${unitSystem === key ? 'weather-unit-button--active' : ''}`}
                      onClick={() => setUnitSystem(key)}
                      aria-pressed={unitSystem === key}
                      aria-label={preset.label}
                    >
                      {key === 'imperial' ? 'F' : 'C'}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="weather-refresh-button weather-refresh-button--icon"
                onClick={() => setRefreshKey((current) => current + 1)}
                disabled={(!hasConfig && !hasHistoryConfig) || refreshInProgress}
                aria-label={refreshInProgress ? 'Refreshing weather data' : 'Refresh weather data'}
                title={refreshInProgress ? 'Refreshing weather data' : 'Refresh weather data'}
              >
                <RefreshIcon spinning={refreshInProgress} />
              </button>
            </div>
          </header>

          <div className="weather-station-hero-section">
            <div className={`weather-current-strip weather-current-strip--${weatherSummary.condition}`}>
              <div className="weather-current-primary">
                <div className="weather-current-icon">
                  <span className="weather-current-icon-label">Latest Conditions</span>
                  <MeshtasticWeatherIconSummary
                    weatherData={weatherSummaryBlock}
                    timestamp={weatherSummaryTimestamp}
                  />
                </div>
                <div className="weather-current-temp">
                  <span className="weather-current-temp-value">{quickTempValue}</span>
                  <span className="weather-current-feels-like">
                    Feels like {quickFeelsLikeValue}
                  </span>
                  <span className="weather-current-summary-title">{visibleWeatherSummaryLabel}</span>
                  <p className="weather-current-summary-text">{visibleWeatherSummaryTitle}</p>
                </div>
              </div>

              <div className="weather-current-metrics" aria-label="Current conditions">
                <div className="weather-current-metric">
                  <WeatherUiIcon name="humidity" className="weather-current-metric-icon" />
                  <span className="weather-current-metric-value">{quickHumidityValue}</span>
                  <span className="weather-current-eyebrow">Humidity</span>
                </div>
                <div className="weather-current-metric">
                  <WeatherUiIcon name="wind" className="weather-current-metric-icon" />
                  <span className="weather-current-metric-value">{quickWindValue}</span>
                  <span className="weather-current-eyebrow">Wind</span>
                </div>
                <div className="weather-current-metric">
                  <WeatherUiIcon name="pressure" className="weather-current-metric-icon" />
                  <span className="weather-current-metric-value">{quickPressureValue}</span>
                  <span className="weather-current-eyebrow">Pressure</span>
                </div>
              </div>

              <div className="weather-current-status-grid">
                <div
                  className="weather-current-status-card weather-current-status-card--health weather-tooltip-anchor"
                  role="status"
                  tabIndex={0}
                  aria-label={`Station health is ${weatherHealth.label}. ${weatherHealth.note}`}
                >
                  <div className="weather-current-status-header">
                    <span className="weather-current-status-icon" aria-hidden="true">
                      <WeatherUiIcon name="health" />
                    </span>
                    <span className="weather-current-eyebrow">Station Health</span>
                  </div>
                  <div className="weather-health-card-stack">
                    <span className={`weather-health-badge weather-health-badge--${weatherHealth.tone}`}>
                      <span className={`weather-health-dot weather-health-dot--${weatherHealth.tone}`} aria-hidden="true" />
                      {weatherHealth.label}
                    </span>
                    <span
                      className={`weather-health-battery weather-health-battery--${batteryVoltageState.tone}`}
                      aria-label={`${batteryVoltageState.label}: ${quickBatteryVoltageValue}. ${batteryVoltageState.note}`}
                    >
                      <WeatherUiIcon name="battery" />
                      <span>{quickBatteryVoltageValue}</span>
                      <span className="weather-health-battery-label">{batteryVoltageState.label}</span>
                    </span>
                  </div>
                  <span className="weather-tooltip">
                    Green: station is online with a reading received within 10 minutes. Yellow:
                    signal is delayed between 10 minutes and 1 hour. Red: station is offline with
                    no reading in the last hour.
                  </span>
                </div>
                <div className="weather-current-status-card weather-current-status-card--latest">
                  <div className="weather-current-status-header">
                    <span className="weather-current-status-icon" aria-hidden="true">
                      <WeatherUiIcon name="clock" />
                    </span>
                    <span className="weather-current-eyebrow">Latest Reading</span>
                  </div>
                  <div className="weather-current-reading-stack">
                    <span className="weather-current-date">{latestReadingDetails.dateLabel}</span>
                    {latestReadingDetails.timeLabel && (
                      <span className="weather-current-time">{latestReadingDetails.timeLabel}</span>
                    )}
                    <span className="weather-current-status-note">{latestReadingDetails.ageLabel}</span>
                  </div>
                </div>
              </div>

              {isDailyWeatherSummaryLoading ? (
                <div className="weather-current-daily weather-current-daily--loading" aria-label="Today so far loading">
                  <span className="weather-current-daily-label">Today so far</span>
                  <div className="weather-current-daily-loading" role="status" aria-live="polite">
                    <span className="weather-spinner weather-spinner--small" />
                    <span>Loading...</span>
                  </div>
                </div>
              ) : dailyWeatherSummary && (
                <div className="weather-current-daily" aria-label="Today so far">
                  <span className="weather-current-daily-label">Today so far</span>
                  {dailyWeatherSummary.items.length > 0 && (
                    <dl className="weather-current-daily-grid">
                      {dailyWeatherSummary.items.map((item) => (
                        <div
                          key={item.key}
                          className={`weather-current-daily-item weather-current-daily-item--${item.key}`}
                        >
                          <WeatherUiIcon
                            name={getDailySummaryIconName(item.key)}
                            className="weather-current-daily-item-icon"
                          />
                          <div className="weather-current-daily-item-copy">
                            <dt className="weather-current-daily-item-label">{item.label}</dt>
                            <dd className="weather-current-daily-item-value">{item.value}</dd>
                          </div>
                        </div>
                      ))}
                    </dl>
                  )}
                  {dailyWeatherSummary.trendText && (
                    <p className="weather-current-daily-trend">{dailyWeatherSummary.trendText}</p>
                  )}
                </div>
              )}
              {sunTimes && (
                <a
                  className="weather-current-sun-attribution"
                  href={sunTimes.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Sun times: {sunTimes.sourceName}
                </a>
              )}
            </div>
          </div>

          {missingConfigKeys.length > 0 && (
            <div className="weather-inline-alert weather-inline-alert--warning">
              Add {missingConfigKeys.map((key) => `\`${key}\``).join(', ')} to your environment
              configuration, then restart the app.
            </div>
          )}

          {error && (
            <div className="weather-inline-alert weather-inline-alert--danger">
              {error}
              <br />
              If this endpoint is blocked by CORS or the key must stay private, the next step is to
              proxy the request through a backend.
            </div>
          )}

          <div ref={historyChartContainerRef} id="weather-history">
            <MeshtasticWeatherHistoryChart
              historyItems={historyItems}
              chartWidth={historyChartWidth}
              historyMeta={historyMeta}
              historySummary={historyBackendSummary.status === 'available' ? historyBackendSummary.summary : null}
              loading={historyLoading}
              error={historyError}
              selectedWindowKey={selectedHistoryWindowKey}
              temperatureUnit={temperatureUnit}
              windSpeedUnit={windSpeedUnit}
              precipitationUnit={precipitationUnit}
              pressureUnit={pressureUnit}
              windowOptions={HISTORY_WINDOW_OPTIONS}
              selectedWindow={selectedHistoryWindow}
              customWindowRange={customHistoryWindowRange}
              rainTotalLoading={historyRainTotal.isLoading}
              rainTotalOverride={historyRainTotalValue}
              onWindowChange={handleHistoryWindowChange}
              onCustomWindowApply={applyCustomHistoryWindowRange}
              onZoomRangeSelect={applyCustomHistoryWindowRange}
              canExportCsv={hasHistoryConfig && Boolean(historyStationId)}
              csvExportError={historyCsvExport.error}
              csvExporting={historyCsvExport.isExporting}
              csvExportMessage={historyCsvExport.message}
              csvDownloadUrl={historyCsvExport.downloadUrl}
              csvFilename={historyCsvExport.fileName}
              onExportCsv={handleHistoryCsvExport}
              onExportChartCsv={handleChartCsvExport}
            />
          </div>

          <div className="weather-section-stack">
            <WeatherPanel
              id="weather-readings"
              title="Latest Weather Readings"
              icon="readings"
              collapsible
              defaultOpen={false}
              isOpen={readingsPanelOpen}
              onOpenChange={setReadingsPanelOpen}
            >
              <p className="weather-summary-legend">
                Status colors flag normal, caution, and extreme readings.
              </p>

              {loading && !observation ? (
                <div className="weather-loading">
                  <span className="weather-spinner" />
                </div>
              ) : weatherSummaryItems.length > 0 ? (
                <div className="weather-summary-grid">
                  {weatherSummaryItems.map((item) => (
                    <div
                      key={item.label}
                      className={`weather-summary-card weather-summary-card--${item.tone || 'info'}`}
                    >
                      <div className="weather-summary-top">
                        <div className="weather-summary-heading">
                          <WeatherUiIcon name="details" className="weather-summary-icon" />
                          <p className="weather-summary-label">{item.label}</p>
                        </div>
                        <span className={`weather-summary-badge weather-summary-badge--${item.tone || 'info'}`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="weather-summary-value">{item.value}</p>
                      <p className="weather-summary-note">{item.note}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="weather-empty">
                  The request completed, but there was no `item.weather` block in the response yet.
                </div>
              )}
            </WeatherPanel>

            <WeatherPanel
              id="weather-details"
              title="Weather Sensor Details"
              icon="details"
              collapsible
              defaultOpen={false}
              isOpen={detailsPanelOpen}
              onOpenChange={setDetailsPanelOpen}
            >
              {weatherDetailItems.length > 0 ? (
                <div className="weather-detail-grid">
                  {weatherDetailItems.map((entry) => (
                    <div key={entry.path} className="weather-detail-card">
                      <p className="weather-detail-label">
                        <code>{entry.label}</code>
                      </p>
                      <p className="weather-detail-value">{entry.value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="weather-empty">
                  A successful response will list every field from the `item.weather` object here.
                </div>
              )}
            </WeatherPanel>

            <WeatherPanel
              id="weather-shared-data"
              title="Shared Weather Data"
              icon="globe"
            >
              <div className="weather-sharing-content">
                <p className="weather-sharing-copy">
                  We contribute station readings to NOAA's MADIS network as citizen scientists
                  and share them with Weather Underground and Windy so neighbors, gardeners, and
                  other weather watchers can use the local data.
                </p>
                <div className="weather-sharing-links">
                  <a
                    className="weather-project-footer__link weather-sharing-link"
                    href={WEATHER_STATION_NOAA_MADIS_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <WeatherUiIcon name="globe" className="weather-link-icon" />
                    Open NOAA MADIS station G7606
                  </a>
                  <a
                    className="weather-project-footer__link weather-sharing-link"
                    href={WEATHER_STATION_PUBLIC_DASHBOARD_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <WeatherUiIcon name="globe" className="weather-link-icon" />
                    Open Weather Underground dashboard
                  </a>
                  <a
                    className="weather-project-footer__link weather-sharing-link"
                    href={WEATHER_STATION_WINDY_DASHBOARD_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <WeatherUiIcon name="globe" className="weather-link-icon" />
                    Open Windy dashboard
                  </a>
                </div>
              </div>
            </WeatherPanel>
          </div>

          <div className="weather-project-footer" id="weather-project">
            <a
              className="weather-project-footer__link"
              href={WEATHER_STATION_PROJECT_URL}
              target="_blank"
              rel="noreferrer"
            >
              <WeatherUiIcon name="article" className="weather-link-icon" />
              View the Hackaday build log
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default MeshtasticWeatherStation;
